import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useAdditionalFieldsController } from 'api/controllers/AdditionalFieldsController';
import { useCounterPartyController } from 'api/controllers/CounterPartyController';

import {
  CounterpartyCreateUpdate,
  CounterpartyType,
  ExpenseType
} from 'openapi';

import { AddCounterPartyPanel } from 'components/Counterparties/AddCounterParty/AddCounterPartyPanel';

import { useTranslations } from 'context/TranslationContext';

import { AppRoutesEnum } from 'utils/routes';

export const AddCounterparty = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { translate } = useTranslations();
  const { getExpenseTypes } = useAdditionalFieldsController();
  const { createNewCounterParty, getFilteredCounterpartiesByCompanyId } =
    useCounterPartyController();

  const [expenseTypeOptions, setExpenseTypeOptions] = useState<ExpenseType[]>(
    []
  );

  useEffect(() => {
    const fetchExpenseTypes = async () => {
      if (!companyId) return;

      const data = await getExpenseTypes(Number(companyId));
      setExpenseTypeOptions(data);
    };

    fetchExpenseTypes();
  }, [companyId, getExpenseTypes]);

  const handleBack = () => {
    if (!companyId) return;

    navigate(
      AppRoutesEnum.COMPANY_COUNTERPARTIES.replace(':companyId', companyId)
    );
  };

  const checkNameExists = useCallback(
    async (name: string, type: CounterpartyType) => {
      if (!companyId || !name.trim()) {
        return false;
      }

      const result = await getFilteredCounterpartiesByCompanyId(
        Number(companyId),
        {
          names: [name.trim().toLowerCase()],
          types: [type],
          pageNumber: 1,
          pageSize: 10
        }
      );

      return (result?.counterparties?.length ?? 0) > 0;
    },
    [companyId, getFilteredCounterpartiesByCompanyId]
  );

  const handleSave = async (values: CounterpartyCreateUpdate) => {
    if (!companyId) return;

    try {
      await createNewCounterParty(Number(companyId), values);
      handleBack();
    } catch (error) {
      console.error('Failed to create counterparty:', error);
    }
  };

  return companyId ? (
    <AddCounterPartyPanel
      headerTitle={translate('labels.newCounterparty')}
      expenseTypeOptions={expenseTypeOptions}
      checkNameExists={checkNameExists}
      onClose={handleBack}
      onSubmit={handleSave}
    />
  ) : null;
};
